import { skipToken as reactQuerySkipToken } from "@tanstack/react-query";
import {
  useCurrentWorkspaceContext,
  useCurrentWorkspaceId,
  useIsAgentBillingEnabled,
} from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { agentBillingRouter } from "@v2/trpc/routes/agentBilling";
import { analyticsRouter } from "@v2/trpc/routes/analytics";
import { subscriptionRouter } from "@v2/trpc/routes/subscription";

const useAnalyticsStatus = () => {
  const workspaceId = useCurrentWorkspaceId() ?? undefined;
  const projectId = useCurrentProjectId();
  const { isLoading: isLoadingWorkspace } = useCurrentWorkspaceContext();
  const isAgentBilling = useIsAgentBillingEnabled();

  const { data: isEnabled, isLoading: isLoadingEnableCheck } =
    analyticsRouter.isEnabled.useQuery(
      workspaceId || projectId
        ? { workspaceId, projectId }
        : reactQuerySkipToken,
    );

  const { data: subscription, isLoading: isLoadingSubscription } =
    subscriptionRouter.getActiveSubscription.useQuery(
      workspaceId && !isAgentBilling
        ? { workspaceId, projectId }
        : reactQuerySkipToken,
    );

  // NOTE (Yuxin, 2026-04-01, USE-2422): Agent-billing workspaces store
  // subscriptions in stripe_subscription, not the legacy subscription table.
  // Check the agent-billing endpoint for these workspaces.
  const {
    data: agentBillingSubscription,
    isLoading: isLoadingAgentBillingSubscription,
  } = agentBillingRouter.subscription.find.useQuery(
    workspaceId && isAgentBilling ? { workspaceId } : reactQuerySkipToken,
  );

  const isCurrentlyPaying = isAgentBilling
    ? Boolean(agentBillingSubscription)
    : Boolean(subscription && subscription.billingTier !== "free");

  const isLoading =
    isLoadingWorkspace ||
    isLoadingEnableCheck ||
    isLoadingSubscription ||
    isLoadingAgentBillingSubscription;

  const dataDisplayType = (() => {
    if (isEnabled && !isCurrentlyPaying) {
      return "partialData";
    } else if (!isEnabled && isCurrentlyPaying) {
      return "blurFullScreenRequireShopifyConnect";
    } else if (!isEnabled && !isCurrentlyPaying) {
      return "blurFullScreenRequireUpgrade";
    } else {
      return "normal";
    }
  })();

  return {
    isEnabled,
    isCurrentlyPaying,
    isLoading,
    dataDisplayType,
    shouldBlurFullScreen:
      dataDisplayType === "blurFullScreenRequireShopifyConnect" ||
      dataDisplayType === "blurFullScreenRequireUpgrade",
  };
};

export default useAnalyticsStatus;
